import '@webcomponents/scoped-custom-element-registry';
import 'element-internals-polyfill';
import { AutoLoader } from '@cigna/dcws_leaf-web-components/utilities/autoloader.js';
import { registerIconSet } from '@cigna/dcws_leaf-web-components/utilities/icon-library.js';
import { scrollToAnchor } from '@cigna/dcws_leaf-web-components/utilities/anchor.js';
import { GenericContextProvider as ContextProvider } from '@cigna/dcws_leaf-web-components/utilities/context/generic-provider.js';
import '@cigna/dcws_leaf-web-components/utilities/translations/es.js';
import { ContextRoot } from '@lit-labs/context';
import * as contexts from './apps/contexts.js';
import { initialize as initURLSelects } from './apps/url-select-form/service.js';
import { initialize as initHideShowSelect } from './apps/hide-show-select-form/service.js';
import { initialize as initContactUsState } from './apps/contact-us-state/index.js';
import { CampaignsService } from './apps/campaigns/service.js';
import { personalizeContent } from './apps/personalized-content/service.js';
import { components } from './components.js';
import { environment } from './environments/environment.js';
import { fixExternalLinks } from './utils/fix-links.js';
import { handleEloquaForms } from './utils/forms.js';
import { initI18n } from './utils/i18n.js';
import { setInlineClasses } from './utils/inline-class.js';
import { queryShadowRoot } from './utils/query-shadow-root.js';
import { waitFor } from './utils/wait.js';
import { updateDynamicLinks } from './apps/campaigns/link-service.js';
import { setupClickTracking, createImpression } from './analytics/index.js';

// Bootstrap the application

// Register icons sets
registerIconSet('logos', (name: string) => `/static/svgs/logos/${name}-logo.svg`);
registerIconSet('chc', (name: string) => `/static/svgs/chc/${name}.svg`);
registerIconSet('social', (name: string) => `/static/svgs/chc/social/${name}.svg`);

// add personalization early
personalizeContent(document);

const autoLoader = new AutoLoader(components, { logger: environment.logger, mode: 'registered' });
if (import.meta.env.MODE === 'localdev') {
  autoLoader.components.set('example-element', {
    register() {
      return import('./apps/i18n-example/example.js');
    },
  });
}
autoLoader.discover(document.body).then(() => {
  // <meta name="autoloader" content="stopafterload"> = stop autoloader after initial load
  const autoloaderMeta = document.querySelector('meta[name="autoloader"]');
  if (autoloaderMeta?.getAttribute('content') === 'stopafterload') {
    autoLoader.stop();
  }
});

// styling adjustments for Teamsite added markup
setInlineClasses();

fixExternalLinks();
handleEloquaForms(environment.flags.enableEloquaTestEmails, environment.logger);
initURLSelects();
initHideShowSelect();

// Provide config, functions, and services down to components
const contextProvider = new ContextProvider(document.body);
new ContextRoot().attach(document.body);

contextProvider.provide(contexts.endpointsContext, environment.endpoints);
contextProvider.provide(contexts.i18nContext, initI18n(environment.i18n));

const campaignsService = new CampaignsService(environment.endpoints.campaigns, environment.logger);
campaignsService.getCampaignsInfo().then((campaignInfo) => {
  try {
    contextProvider.provide(contexts.dynamicPhoneNumberContext, campaignInfo['campaignPhone-cipublic']);
    contextProvider.provide(contexts.dynamicLinksContext, campaignInfo);

    // TODO: Think about reworking this
    queryShadowRoot(document.body).forEach((link) => {
      const href = link.getAttribute('href');
      if (href) {
        const newLinkHref = updateDynamicLinks(href, campaignInfo);
        if (newLinkHref && newLinkHref.length) {
          link.setAttribute('href', newLinkHref);
        }
      }
    });
  } catch (error) {
    environment.logger?.error(error);
  }
});

// Setup analytics
// tell apps that Adobe is loaded/ready to listen for events, remove 3000 to wait indefinitely
waitFor(() => Object.prototype.hasOwnProperty.call(window, '_satellite'), {
  timeout: 3000,
  logger: environment.logger,
}).then(() => {
  environment.logger?.info('analytics ready', window._satellite);
  contextProvider.provide(contexts.analyticsReadyContext, true);
});
setupClickTracking(createImpression, environment.logger);

window.addEventListener('load', () => {
  initContactUsState();
  scrollToAnchor();
});
